import React, { useEffect, useRef } from 'react';
import { AlertCircle, X } from 'lucide-react';

/**
 * Enhanced error message component with auto-focus and scroll behavior
 */
const ErrorMessage = React.forwardRef(({
  error,
  errors = {},
  onClear,
  autoFocus = true,
  className = '',
  showIcon = true,
  variant = 'danger' // 'danger', 'warning', 'info'
}, ref) => {
  const internalRef = useRef(null);
  const errorRef = ref || internalRef;

  // Auto-scroll to error when it appears
  useEffect(() => {
    if ((error || Object.keys(errors).length > 0) && autoFocus && errorRef.current) {
      setTimeout(() => {
        errorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [error, errors, autoFocus]);

  // Don't render if no errors
  if (!error && Object.keys(errors).length === 0) {
    return null;
  }

  const variantStyles = {
    danger: {
      container: 'bg-red-50 border-red-200 text-red-800',
      icon: 'text-red-500',
      button: 'text-red-500 hover:text-red-700'
    },
    warning: {
      container: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      icon: 'text-yellow-500',
      button: 'text-yellow-500 hover:text-yellow-700'
    },
    info: {
      container: 'bg-blue-50 border-blue-200 text-blue-800',
      icon: 'text-blue-500',
      button: 'text-blue-500 hover:text-blue-700'
    }
  };

  const styles = variantStyles[variant];

  return (
    <div
      ref={errorRef}
      className={`rounded-lg border p-4 ${styles.container} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        {showIcon && (
          <div className="flex-shrink-0">
            <AlertCircle className={`h-5 w-5 ${styles.icon}`} />
          </div>
        )}

        <div className={`${showIcon ? 'ml-3' : ''} flex-1`}>
          {/* General error message */}
          {error && (
            <div className="text-sm font-medium">
              {error}
            </div>
          )}

          {/* Field-specific errors */}
          {Object.keys(errors).length > 0 && (
            <div className="mt-2">
              {error && <div className="text-sm font-medium mb-2">Please correct the following errors:</div>}
              <ul className="list-disc list-inside space-y-1 text-sm">
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field}>
                    <span className="font-medium capitalize">
                      {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </span>{' '}
                    {message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Close button */}
        {onClear && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onClear}
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.button}`}
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ErrorMessage.displayName = 'ErrorMessage';

/**
 * Field-specific error message component
 */
export const FieldError = ({ error, className = '' }) => {
  if (!error) return null;

  return (
    <p className={`mt-1 text-sm text-red-600 ${className}`} role="alert">
      {error}
    </p>
  );
};

/**
 * Success message component
 */
export const SuccessMessage = React.forwardRef(({
  message,
  onClear,
  autoFocus = true,
  className = '',
  showIcon = true
}, ref) => {
  const internalRef = useRef(null);
  const messageRef = ref || internalRef;

  useEffect(() => {
    if (message && autoFocus && messageRef.current) {
      setTimeout(() => {
        messageRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [message, autoFocus]);

  if (!message) return null;

  return (
    <div
      ref={messageRef}
      className={`rounded-lg border border-green-200 bg-green-50 p-4 text-green-800 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        {showIcon && (
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        <div className={`${showIcon ? 'ml-3' : ''} flex-1`}>
          <div className="text-sm font-medium">
            {message}
          </div>
        </div>

        {onClear && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onClear}
                className="inline-flex rounded-md p-1.5 text-green-500 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2"
                aria-label="Dismiss message"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

SuccessMessage.displayName = 'SuccessMessage';

/**
 * Loading message component
 */
export const LoadingMessage = ({ message = 'Processing request...', className = '' }) => {
  return (
    <div className={`rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-800 ${className}`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        </div>
        <div className="ml-3">
          <div className="text-sm font-medium">
            {message}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorMessage;
